import type { MediaVariant } from "../../types";

export type StoredMediaVariantPreference = Pick<
  MediaVariant,
  | "id"
  | "kind"
  | "label"
  | "format"
  | "quality"
  | "resolution"
  | "bitrate"
  | "codec"
>;

export type PlaybackVariantPreference = {
  videoMode: "auto" | "variant";
  video?: StoredMediaVariantPreference;
  audioMode: "auto" | "variant";
  audio?: StoredMediaVariantPreference;
};

const STORAGE_KEY = "neptunePlaybackVariantPreferencesV1";
const DEFAULT_PREFERENCE: PlaybackVariantPreference = {
  videoMode: "auto",
  audioMode: "auto",
};
let memoryPreferences: Record<string, PlaybackVariantPreference> = {};

const cleanText = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const normalizedVariant = (
  value: unknown,
): StoredMediaVariantPreference | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const entry = value as Record<string, unknown>;
  const kind = cleanText(entry.kind);
  if (kind !== "mobile" && kind !== "desktop" && kind !== "audio") {
    return undefined;
  }
  const id = cleanText(entry.id);
  if (!id) return undefined;
  return {
    id,
    kind,
    label: cleanText(entry.label),
    format: cleanText(entry.format),
    quality: cleanText(entry.quality),
    resolution: cleanText(entry.resolution),
    bitrate: cleanText(entry.bitrate),
    codec: cleanText(entry.codec),
  };
};

const normalizedPreference = (
  value: unknown,
): PlaybackVariantPreference | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const entry = value as Record<string, unknown>;
  const video = normalizedVariant(entry.video);
  const audio = normalizedVariant(entry.audio);
  const videoMode =
    entry.videoMode === "auto"
      ? "auto"
      : entry.videoMode === "variant" && video
        ? "variant"
        : video
          ? "variant"
          : "auto";
  const audioMode = entry.audioMode === "variant" && audio ? "variant" : "auto";
  return {
    videoMode,
    video: videoMode === "variant" ? video : undefined,
    audioMode,
    audio: audioMode === "variant" ? audio : undefined,
  };
};

const readAllPreferences = () => {
  if (typeof window === "undefined") return memoryPreferences;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return memoryPreferences;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") return memoryPreferences;
    const normalized: Record<string, PlaybackVariantPreference> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(
      ([owner, value]) => {
        const preference = normalizedPreference(value);
        if (owner && preference) normalized[owner] = preference;
      },
    );
    memoryPreferences = normalized;
  } catch {
    // Keep the in-memory copy when browser storage is unavailable or malformed.
  }
  return memoryPreferences;
};

const writeAllPreferences = (
  preferences: Record<string, PlaybackVariantPreference>,
) => {
  memoryPreferences = preferences;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // The in-memory copy still preserves the preference for this session.
  }
};

export const playbackPreferenceOwner = (
  userId?: string | null,
  visitorId?: string | null,
) => {
  const signedInUser = cleanText(userId);
  if (signedInUser) return `user:${signedInUser}`;
  return `visitor:${cleanText(visitorId) || "anonymous"}`;
};

export function readPlaybackVariantPreference(
  owner: string,
): PlaybackVariantPreference {
  return readAllPreferences()[owner] || { ...DEFAULT_PREFERENCE };
}

export function rememberVideoVariantPreference(
  owner: string,
  variant: MediaVariant | null,
) {
  const preferences = { ...readAllPreferences() };
  const current = preferences[owner] || DEFAULT_PREFERENCE;
  preferences[owner] = {
    ...current,
    videoMode: variant ? "variant" : "auto",
    video: variant ? normalizedVariant(variant) : undefined,
  };
  writeAllPreferences(preferences);
  return preferences[owner];
}

export function rememberAudioVariantPreference(
  owner: string,
  variant: MediaVariant | null,
) {
  const preferences = { ...readAllPreferences() };
  const current = preferences[owner] || DEFAULT_PREFERENCE;
  preferences[owner] = variant
    ? {
        ...current,
        audioMode: "variant",
        audio: normalizedVariant(variant),
      }
    : {
        ...current,
        audioMode: "auto",
        audio: undefined,
      };
  writeAllPreferences(preferences);
  return preferences[owner];
}

const normalized = (value: string) => value.trim().toLowerCase();
const numericPart = (value: string) => {
  const matched = value.match(/\d+(?:\.\d+)?/);
  return matched ? Number(matched[0]) : Number.NaN;
};

/**
 * Finds the same named variant on another video. When that exact variant is
 * missing, semantic fields (quality, compression/bitrate and resolution) are
 * used so the closest available version is selected instead of forgetting the
 * viewer's choice.
 */
export function matchPlaybackVariantPreference(
  variants: MediaVariant[],
  preference?: StoredMediaVariantPreference,
): MediaVariant | undefined {
  if (!preference || variants.length === 0) return undefined;
  const exact = variants.find((variant) => variant.id === preference.id);
  if (exact) return exact;

  const preferredQuality = normalized(preference.quality);
  const preferredResolution = normalized(preference.resolution);
  const preferredBitrate = normalized(preference.bitrate);
  const preferredFormat = normalized(preference.format);
  const preferredCodec = normalized(preference.codec);
  const preferredLabel = normalized(preference.label);
  const preferredQualityNumber = numericPart(preference.quality);
  const preferredBitrateNumber = numericPart(preference.bitrate);

  return [...variants]
    .map((variant, index) => {
      let score = 0;
      const quality = normalized(variant.quality);
      const resolution = normalized(variant.resolution);
      const bitrate = normalized(variant.bitrate);
      if (preferredQuality && quality === preferredQuality) score += 500;
      if (preferredBitrate && bitrate === preferredBitrate) score += 300;
      if (preferredResolution && resolution === preferredResolution)
        score += 180;
      if (variant.kind === preference.kind) score += 100;
      if (preferredFormat && normalized(variant.format) === preferredFormat)
        score += 30;
      if (preferredCodec && normalized(variant.codec) === preferredCodec)
        score += 20;
      if (preferredLabel && normalized(variant.label) === preferredLabel)
        score += 10;

      const qualityNumber = numericPart(variant.quality);
      if (
        Number.isFinite(preferredQualityNumber) &&
        Number.isFinite(qualityNumber)
      ) {
        score += Math.max(
          0,
          120 - Math.abs(preferredQualityNumber - qualityNumber) / 5,
        );
      }
      const bitrateNumber = numericPart(variant.bitrate);
      if (
        Number.isFinite(preferredBitrateNumber) &&
        Number.isFinite(bitrateNumber)
      ) {
        score += Math.max(
          0,
          80 - Math.abs(preferredBitrateNumber - bitrateNumber),
        );
      }
      return { variant, score, index };
    })
    .sort(
      (left, right) => right.score - left.score || left.index - right.index,
    )[0]?.variant;
}
